/**
 * TaskCard
 * Card para exibir uma tarefa individual
 */

import { Task, TASK_PRIORITY_LABELS, TASK_PRIORITY_COLORS, TASK_STATUS_LABELS, TASK_STATUS_COLORS, TASK_CATEGORY_ICONS } from '@/types/tasks';
import { Calendar, User, CheckCircle2, Circle, XCircle, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CopyableProcessNumber } from '@/components/ui/CopyableProcessNumber';
import { formatTaskDueDate, getDaysUntilTaskDue, isTaskOverdue } from '@/utils/task-date';
import { parseDbTimestamp } from '@/utils/datetime';

export interface TaskCardProps {
  task: Task;
  onClick?: () => void;
  onStatusChange?: (taskId: number, newStatus: string) => void;
  showClient?: boolean;
  showCase?: boolean;
}

export function TaskCard({
  task,
  onClick,
  onStatusChange,
  showClient = false,
  showCase = false
}: TaskCardProps) {

  // Check if task is overdue
  const isOverdue = isTaskOverdue(task);

  // Format due date
  const formatDueDate = (dateStr: string) => {
    const diffDays = getDaysUntilTaskDue(dateStr);

    if (diffDays === null) return 'Data inválida';

    if (diffDays === 0) return 'Hoje';
    if (diffDays === 1) return 'Amanhã';
    if (diffDays === -1) return 'Ontem';
    if (diffDays < -1) return `Atrasada ${Math.abs(diffDays)} dias`;
    if (diffDays > 1 && diffDays <= 7) return `Em ${diffDays} dias`;

    return formatTaskDueDate(dateStr);
  };

  const handleStatusToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStatusChange) {
      const newStatus = task.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
      onStatusChange(task.id, newStatus);
    }
  };

  return (
    <div
      onClick={onClick}
      className={`
        bg-card border rounded-lg p-4
        ${onClick ? 'cursor-pointer hover:shadow-md' : ''}
        ${isOverdue ? 'border-destructive/40 bg-destructive/5' : 'border-border'}
        transition-shadow duration-200
      `}
    >
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        {onStatusChange && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleStatusToggle}
                className="mt-0.5"
                aria-label={task.status === 'COMPLETED' ? 'Marcar como pendente' : 'Marcar como concluída'}
              >
                {task.status === 'COMPLETED' ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : task.status === 'CANCELLED' ? (
                  <XCircle className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{task.status === 'COMPLETED' ? 'Marcar como pendente' : 'Marcar como concluída'}</TooltipContent>
          </Tooltip>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title & Category Icon */}
          <div className="flex items-start gap-2 mb-2">
            {task.category && (
              <span className="text-lg" title={task.category}>
                {TASK_CATEGORY_ICONS[task.category]}
              </span>
            )}
            <h3 className={`text-sm font-semibold ${task.status === 'COMPLETED' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
              {task.title}
            </h3>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
              {task.description}
            </p>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            {/* Due Date */}
            <div className={`flex items-center gap-1 ${isOverdue ? 'text-destructive font-semibold' : ''}`}>
              <Calendar className="h-3 w-3" />
              <span>{formatDueDate(task.due_date)}</span>
              {isOverdue && <span className="text-destructive">⚠️</span>}
            </div>

            {/* Assigned User */}
            {task.assigned_user_name && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{task.assigned_user_name}</span>
              </div>
            )}

            {/* Client */}
            {showClient && task.client_name && (
              <div className="flex items-center gap-1">
                <span>👤</span>
                <span>{task.client_name}</span>
              </div>
            )}

            {/* Case */}
            {showCase && task.case_title && (
              <div className="flex items-center gap-1">
                <span>📂</span>
                {task.case_number
                  ? <CopyableProcessNumber number={task.case_number} />
                  : <span>{task.case_title}</span>
                }
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mt-3">
            {/* Priority Badge */}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${TASK_PRIORITY_COLORS[task.priority]}`}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </span>

            {/* Status Badge */}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${TASK_STATUS_COLORS[task.status]}`}>
              {TASK_STATUS_LABELS[task.status]}
            </span>

            {/* Overdue Badge */}
            {isOverdue && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                <Clock className="h-3 w-3" />
                Atrasada
              </span>
            )}
          </div>

          {/* Completed Info */}
          {task.status === 'COMPLETED' && task.completed_at && (
            <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
              {(() => {
                const completedAt = parseDbTimestamp(task.completed_at);
                return completedAt
                  ? `Concluída ${formatDistanceToNow(completedAt, { addSuffix: true, locale: ptBR })}`
                  : 'Concluída';
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
